import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../../components/ui/Sidebar";
import Sidebar from "../../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";
import { useCreateExpense } from "./useCreateExpense";
import { buildExpensePayload } from "./logic/buildExpensePayload";
import { useGetVendors } from "pages/vendor-management/useGetVendors";

const CATEGORIES = [
  "Fixed Assets",
  "IT Equipment",
  "Furniture",
  "Machinery",
  "Vehicles",
  "Communication Equipment",
  "Medical Equipment",
  "Other",
];

const IMPORT_SUBTYPES = [
  { value: "ec_good", label: "EC (Good)" },
  { value: "ec_service", label: "EC (Service)" },
  { value: "non_ec", label: "Non EC" },
];

const EMPTY = {
  vendorId: "",
  date: new Date().toISOString().split("T")[0],
  expenseType: "domestic",
  importSubtype: "",
  category: "",
  vendorName: "",
  assetName: "",
  amount: "",
  comments: "",
};

const SectionCard = ({
  icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  children,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
  >
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div
          className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}
        >
          <Icon name={icon} size={20} className={iconColor} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground dark:text-white">
            {title}
          </h3>
          <p className="text-sm text-muted-foreground dark:text-gray-300">
            {subtitle}
          </p>
        </div>
      </div>
      {children}
    </div>
  </motion.div>
);

const TextInput = ({ error, className = "", ...props }) => (
  <input
    {...props}
    className={`w-full px-3 py-2.5 border rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${
      error ? "border-red-400" : "border-gray-200 dark:border-gray-600"
    } ${className}`}
  />
);

const SelectInput = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
  >
    {children}
  </select>
);

const Field = ({ label, required, error, hint, children }) => (
  <div>
    {label && (
      <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {hint && !error && (
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    )}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const OfficeEquipmentContent = () => {
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("pages");
  const existing = location.state?.expense || null;

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const createExpense = useCreateExpense();
  const { data: vendors = [] } = useGetVendors();

  useEffect(() => {
    if (existing) setForm({ ...EMPTY, ...existing });
  }, [existing]);

  const set = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.date) e.date = "Date is required.";
    if (!form.category) e.category = "Category is required.";
    if (!form.assetName.trim()) e.assetName = "Asset name is required.";
    if (!form.amount || parseFloat(form.amount) <= 0)
      e.amount = "Amount must be greater than 0.";
    if (form.expenseType === "import" && !form.importSubtype)
      e.importSubtype = "Please select import subtype.";
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      toast.error(t("purchaseExpenseForms.saveError"));
      return;
    }

    setSaving(true);
    try {
      // Asset name is the meaningful line description for equipment; fold it in.
      await createExpense.mutateAsync(
        buildExpensePayload(
          { ...form, category: form.category || form.assetName },
          "office_equipment",
        ),
      );
      toast.success(t("purchaseExpenseForms.equipmentRecorded"));
      navigate("/expenses");
    } catch (err) {
      toast.error(err?.message || t("purchaseExpenseForms.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const amount = parseFloat(form.amount) || 0;

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Sticky Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-40 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-8"
        >
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/expenses")}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="w-6 h-6 bg-teal-100 dark:bg-teal-900/20 rounded flex items-center justify-center">
                    <Icon
                      name="Monitor"
                      size={13}
                      className="text-teal-600 dark:text-teal-400"
                    />
                  </div>
                  <span className="text-xs font-medium text-teal-600 dark:text-teal-400 uppercase tracking-wide">
                    {t("purchaseExpenseForms.officeEquipment")}
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-foreground dark:text-white">
                  {existing
                    ? t("purchaseExpenseForms.editEquipment")
                    : t("purchaseExpenseForms.newEquipment")}
                </h1>
                <p className="text-sm text-muted-foreground dark:text-gray-300">
                  {t("purchaseExpenseForms.equipmentDescription")}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => navigate("/expenses")}
              >
                {t("purchaseExpenseForms.cancel")}
              </Button>
              <Button
                variant="primary"
                size="md"
                iconName={saving ? "Loader2" : "Save"}
                onClick={handleSave}
                disabled={saving}
                className="shadow-sm"
              >
                {saving
                  ? t("purchaseExpenseForms.saving")
                  : existing
                    ? t("purchaseExpenseForms.updateExpense")
                    : t("purchaseExpenseForms.saveExpense")}
              </Button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3 space-y-6">
            {/* Section 1: Expense Type */}
            <SectionCard
              icon="Globe"
              iconBg="bg-blue-100 dark:bg-blue-900/20"
              iconColor="text-blue-600 dark:text-blue-400"
              title="Expense Type"
              subtitle="Is this a domestic or import purchase?"
            >
              <div className="grid grid-cols-2 gap-3 mb-5">
                {["domestic", "import"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      set("expenseType", type);
                      set("importSubtype", "");
                    }}
                    className={`flex items-center gap-3 p-4 rounded-xl border transition-all duration-200 ${
                      form.expenseType === type
                        ? "border-primary bg-primary-50 dark:bg-primary-900/20 text-primary"
                        : "border-gray-200 dark:border-gray-600 hover:border-gray-300 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Icon
                      name={type === "domestic" ? "Home" : "Globe"}
                      size={20}
                    />
                    <div className="text-start">
                      <div className="font-medium capitalize">{type}</div>
                      <div className="text-xs text-muted-foreground">
                        {type === "domestic"
                          ? "Local purchase"
                          : "International import"}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <AnimatePresence>
                {form.expenseType === "import" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <Field
                      label="Import Subtype"
                      required
                      error={errors.importSubtype}
                    >
                      <div className="grid grid-cols-3 gap-3">
                        {IMPORT_SUBTYPES.map((sub) => (
                          <button
                            key={sub.value}
                            type="button"
                            onClick={() => set("importSubtype", sub.value)}
                            className={`p-3 rounded-xl border text-sm font-medium transition-all duration-150 ${
                              form.importSubtype === sub.value
                                ? "border-primary bg-primary-50 dark:bg-primary-900/20 text-primary shadow-sm"
                                : "border-gray-200 dark:border-gray-600 text-muted-foreground hover:border-primary/40 hover:bg-muted/50"
                            }`}
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                      {errors.importSubtype && (
                        <p className="text-xs text-red-500 mt-2">
                          {errors.importSubtype}
                        </p>
                      )}
                    </Field>
                  </motion.div>
                )}
              </AnimatePresence>
            </SectionCard>

            {/* Section 2: Equipment Details */}
            <SectionCard
              icon="Monitor"
              iconBg="bg-teal-100 dark:bg-teal-900/20"
              iconColor="text-teal-600 dark:text-teal-400"
              title="Equipment Details"
              subtitle="Provide the details of the equipment purchased"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Date" required error={errors.date}>
                  <TextInput
                    type="date"
                    value={form.date}
                    error={errors.date}
                    onChange={(e) => set("date", e.target.value)}
                  />
                </Field>

                <Field label="Category" required error={errors.category}>
                  <SelectInput
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                  >
                    <option value="">Select category</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </SelectInput>
                  {errors.category && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.category}
                    </p>
                  )}
                </Field>

                <Field
                  label="Vendor"
                  hint="Link this expense to a vendor (optional)"
                >
                  <SelectInput
                    value={form.vendorId}
                    onChange={(e) => {
                      const v = vendors.find(
                        (x) => String(x.id || x._id) === e.target.value,
                      );
                      set("vendorId", e.target.value);
                      set("vendorName", v?.name || "");
                    }}
                  >
                    <option value="">Select vendor (optional)</option>
                    {vendors.map((v) => (
                      <option key={v.id || v._id} value={v.id || v._id}>
                        {v.name}
                      </option>
                    ))}
                  </SelectInput>
                </Field>

                <Field
                  label="Asset Name"
                  required
                  error={errors.assetName}
                  hint="e.g. Dell XPS 15 Laptop, HP LaserJet Printer"
                >
                  <TextInput
                    value={form.assetName}
                    placeholder="Enter asset name"
                    error={errors.assetName}
                    onChange={(e) => set("assetName", e.target.value)}
                  />
                </Field>

                <Field label="Amount (SR)" required error={errors.amount}>
                  <div className="relative">
                    <span className="absolute start-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      SR
                    </span>
                    <TextInput
                      type="number"
                      value={form.amount}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      error={errors.amount}
                      className="ps-9"
                      onChange={(e) => set("amount", e.target.value)}
                    />
                  </div>
                </Field>

                <div className="md:col-span-2">
                  <Field label="Comments">
                    <textarea
                      value={form.comments}
                      onChange={(e) => set("comments", e.target.value)}
                      placeholder="Additional notes about this equipment purchase..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                    />
                  </Field>
                </div>
              </div>
            </SectionCard>

            {/* Bottom actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 flex justify-end gap-3"
            >
              <Button
                variant="outline"
                size="md"
                onClick={() => navigate("/expenses")}
              >
                {t("purchaseExpenseForms.cancel")}
              </Button>
              <Button
                variant="primary"
                size="md"
                iconName={saving ? "Loader2" : "Save"}
                onClick={handleSave}
                disabled={saving}
              >
                {saving
                  ? t("purchaseExpenseForms.saving")
                  : existing
                    ? t("purchaseExpenseForms.updateExpense")
                    : t("purchaseExpenseForms.saveExpense")}
              </Button>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-4">
              {/* Summary */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Icon name="BarChart2" size={18} className="text-primary" />
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    Summary
                  </h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Asset</span>
                    <span className="font-medium text-foreground truncate max-w-[120px]">
                      {form.assetName || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Category</span>
                    <span className="font-medium text-foreground">
                      {form.category || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Type</span>
                    <span className="font-medium text-foreground capitalize">
                      {form.expenseType}
                      {form.importSubtype
                        ? ` / ${IMPORT_SUBTYPES.find((s) => s.value === form.importSubtype)?.label || ""}`
                        : ""}
                    </span>
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-foreground">
                        Amount
                      </span>
                      <span className="text-lg font-bold text-primary">
                        SR{" "}
                        {amount.toLocaleString("en-SA", {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Checklist */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Icon
                    name="CheckCircle2"
                    size={18}
                    className="text-primary"
                  />
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    Checklist
                  </h3>
                </div>
                <div className="space-y-2.5">
                  {[
                    { label: "Date", ok: !!form.date },
                    { label: "Category", ok: !!form.category },
                    { label: "Asset name", ok: !!form.assetName.trim() },
                    {
                      label: "Amount",
                      ok: !!form.amount && parseFloat(form.amount) > 0,
                    },
                    {
                      label: "Import subtype",
                      ok:
                        form.expenseType === "domestic" || !!form.importSubtype,
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Icon
                          name={item.ok ? "CheckCircle2" : "Circle"}
                          size={15}
                          className={
                            item.ok
                              ? "text-green-500"
                              : "text-gray-300 dark:text-gray-600"
                          }
                        />
                        <span className="text-sm text-muted-foreground">
                          {item.label}
                        </span>
                      </div>
                      <span
                        className={`text-xs font-medium ${item.ok ? "text-green-600 dark:text-green-400" : "text-gray-400"}`}
                      >
                        {item.ok ? "Done" : "Pending"}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OfficeEquipment = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <OfficeEquipmentContent />
    </div>
  </SidebarProvider>
);

export default OfficeEquipment;
